import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/**
 * Skeleton shimmer primitive (BRANDING.md v1.2 § 6.9).
 *
 * Replaces spinners in AI-summary / data-placeholder contexts. Renders a
 * canonical 3–5 line pattern with decreasing widths so the placeholder
 * communicates "structured content is loading here" — not just "something
 * is happening." Animation is opacity-pulse only (single 200ms-equivalent
 * cycle) gated on `motion-safe:` so reduced-motion users see a static
 * grey block.
 *
 * MUST NOT (per spec):
 * - Horizontal-translate gradient shimmer (off-brand "wave" pattern from
 *   2024–25 SaaS templates).
 * - Color cycling (e.g., from grey to white to grey).
 * - Multi-second cycle durations.
 *
 * Use for:
 * - AI-summary panels while the model is generating.
 * - Data tables / lists while the fetch is in-flight.
 * - Card content placeholders.
 *
 * Do NOT use for:
 * - Button-level loading state (use a Loader2 + `motion-safe:animate-spin`
 *   inline icon instead — buttons are immediate-feedback contexts where a
 *   3-line skeleton is the wrong shape).
 * - Auth-check guards (use a static unobtrusive indicator).
 *
 * Source: docs/marketing/BRANDING.md v1.2 § 6.9 (canonical pattern);
 * docs/engineering/contracts/UI_COMPONENT_CONTRACT.md § Token surface
 * "AI-product chip family" + § Canonical primitive set. Migration:
 * ENG-DESIGN-SKELETON-SHIMMER (Wave B · B2 of the 2026-05-09
 * design-system roadmap).
 */

interface SkeletonProps {
  /**
   * Number of placeholder lines to render. Defaults to 3 for the canonical
   * AI-summary placeholder. Clamps to [1, 5] — anything beyond 5 lines is
   * a sign the loading state needs a different visual (e.g., a `Loader2`
   * spinner inside a centered card).
   */
  lines?: number;
  /** Container className — for layout / spacing customisation. */
  className?: string;
}

const LINE_WIDTHS = ["w-full", "w-5/6", "w-3/4", "w-1/2", "w-2/5"] as const;

export default function Skeleton({ lines = 3, className }: SkeletonProps) {
  const { t } = useTranslation();
  const clampedLines = Math.min(Math.max(lines, 1), LINE_WIDTHS.length);
  return (
    <div
      role="status"
      aria-label={t("ui.skeleton.loadingAriaLabel")}
      aria-live="polite"
      className={cn("space-y-2 motion-safe:animate-pulse", className)}
    >
      {Array.from({ length: clampedLines }).map((_, i) => (
        <div
          key={i}
          className={cn("bg-divider-soft h-4 rounded", LINE_WIDTHS[i])}
        />
      ))}
    </div>
  );
}
